import '.v{gap:1rem;}.ssr-plugin'
import { InitiativeCard } from '@app/components/blocks/card/InitiativeCard'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import { useTranslations } from '@i18n/useLanguage'
import {
  topicCommunityTabs,
  topicCommunityTabsQueryParamName,
} from '../constants'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { ViewTopicCommunityModel } from 'functions/app/topic-community/view'
import { pages } from '@app/util/links'
import { fullName, labelAddressBuilder } from '@app/util/helpers'
import Column from '@app/components/layout/Column'
import { DiscussionCard } from '@app/components/blocks/discussions/DiscussionCard'
import useDateFormat from '@app/util/useDateFormat'
import { useQuery } from '@based/sdk/react'
import { ThreadLatestViewModel } from 'functions/app/thread/view-latest'
import useMediaQuery from '@app/util/useMediaQuery'
import Row from '@app/components/layout/Row'
import { styled } from 'inlines'

export const TopicCommunityPageOverview = ({
  topicCommunity,
}: {
  topicCommunity: ViewTopicCommunityModel
}) => {
  const t = useTranslations().app
  const initiatives = topicCommunity?.relatedInitiatives ?? []
  const dateFormat = useDateFormat()
  const isMobile = useMediaQuery()

  const discussionsQuery = useQuery('view-latest-thread', {
    id: topicCommunity.thread?.id,
  })
  const discussions: ThreadLatestViewModel = discussionsQuery.data ?? {
    posts: [],
  }

  return (
    <>
      <SafeHtml html={topicCommunity?.content} />

      {initiatives.length > 0 && (
        <Column alignItems="stretch" gap={0}>
          <SectionHeader
            title={t.topicCommunityPage.featuredInitiatives}
            seeMore={t.common.seeMore}
            toRoute={`?${topicCommunityTabsQueryParamName}=${topicCommunityTabs.initiatives}`}
          />
          <Row
            style={{
              gap: '1rem',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {initiatives.slice(0, 2).map((initiative) => (
              <InitiativeCard
                key={initiative.id}
                to={pages.initiative(initiative.slug)}
                image={initiative.images?.[0] ?? defaultProjectPicture}
                title={initiative.title}
                description={initiative.description}
                location={labelAddressBuilder(
                  initiative.location?.address,
                  t.common.notSpecified,
                )}
                phase={initiative.phase?.name}
                tagText={
                  initiative.searchingForMembers
                    ? t.common.initiativeStatus.searchingForMembers
                    : t.common.initiativeStatus.notSearchingForMembers
                }
                tagVariant={
                  initiative.searchingForMembers ? 'success' : 'neutral'
                }
              />
            ))}
          </Row>
        </Column>
      )}

      {discussions?.posts?.length > 0 && (
        <Column alignItems="stretch" gap={0}>
          <SectionHeader
            title={t.regionPage.latestDiscussions}
            seeMore={t.common.seeMore}
          />
          <div className="v"
            style={{
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {discussions.posts.map((p) => (
              <DiscussionCard
                key={p.id}
                authorName={p.author ? fullName(p.author) : t.common.unknown}
                authorAvatar={p.author?.avatar}
                date={p.createdAt ? dateFormat(new Date(p.createdAt)) : ''}
                content={p.content ?? ''}
                likes={p.viewers?.count ?? 0}
                replies={p.replies?.count ?? 0}
                style={{ flex: 1 }}
              />
            ))}
          </div>
        </Column>
      )}
    </>
  )
}
